import React, { useState } from 'react';
import { History as HistoryIcon, AlertCircle, X, Tag, Trash2, Clipboard, Globe, Lock, Sparkles } from 'lucide-react';
import { formatCodeBlock, minifyExpression } from '@snipit/core/utils/formatters';
import { DEV_LANGUAGES, AI_LANGUAGES, LINK_TYPES, TOOL_TYPES } from '@snipit/core/utils/constants';
import DOMPurify from 'dompurify';
import { marked } from 'marked';
import { generateHashes, convertColor, toCamelCase, toSnakeCase, toPascalCase, toKebabCase, toConstantCase, generateMockUsers, generateLoremIpsum, formatJSON, formatCSV, formatSQL, formatPlainText } from '@snipit/core/utils/toolHelpers';
import { generateCode } from '@snipit/core/services/geminiService';
import { trackEvent } from '@snipit/core/services/analytics';

const GemField = ({ id, label, value, onChange, theme, placeholder }) => (
  <div className="flex flex-col gap-1 shrink-0">
    <label htmlFor={id} className={`text-[10px] font-bold ${theme.textPrimary} uppercase tracking-wider`}>{label}</label>
    <textarea
      id={id} name={id}
      className="w-full bg-black p-2 font-sans text-xs rounded outline-none border border-gray-800 resize-y min-h-15 custom-scrollbar"
      value={value || ''} onChange={e => onChange(e.target.value)} placeholder={placeholder} spellCheck="false"
    />
  </div>
);

const CreateView = ({ newSnippet, setNewSnippet, onSave, onCancel, handleDelete, theme, organization, teams, userLicense }) => {
  const [showHistory, setShowHistory] = useState(false);
  const [tagInput, setTagInput] = useState('');
  const [showMarkdownPreview, setShowMarkdownPreview] = useState(false);
  const [aiPrompt, setAiPrompt] = useState('');
  const [isGenerating, setIsGenerating] = useState(false);
  const [showAiGenerator, setShowAiGenerator] = useState(false);

  // --- Boilerplate Generator State (Added 'constants' tracker) ---
  const [bpOptions, setBpOptions] = useState({
    doGet: true, doPost: true, tryCatch: true, json: true, auth: false, backoff: false, constants: false
  });

  const [mockOptions, setMockOptions] = useState({
    count: 10,
    fields: ['id', 'name', 'email', 'createdAt'],
    format: 'JSON',
    jsonStyle: 'Prettified',
    csvDelimiter: '\\n',
    sqlDialect: 'Postgres',
    previewAsTable: false
  });
  const [mockDataRaw, setMockDataRaw] = useState(null);

  const availableLangs = newSnippet.vault === 'dev' ? DEV_LANGUAGES : newSnippet.vault === 'ai' ? AI_LANGUAGES : newSnippet.vault === 'tools' ? TOOL_TYPES : LINK_TYPES;

  const updateField = (field, value) => setNewSnippet(prev => ({ ...prev, [field]: value }));

  // --- TOOLS: 100% Offline JWT Decoder ---
  const handleDecodeJWT = (token) => {
    updateField('content', token);
    if (!token) return updateField('toolOutput', '');

    try {
      const parts = token.split('.');
      if (parts.length !== 3) throw new Error("Invalid JWT Format");

      const base64Url = parts[1];
      const base64 = base64Url.replace(/-/g, '+').replace(/_/g, '/');
      const jsonPayload = decodeURIComponent(atob(base64).split('').map(function (c) {
        return '%' + ('00' + c.charCodeAt(0).toString(16)).slice(-2);
      }).join(''));

      updateField('toolOutput', JSON.stringify(JSON.parse(jsonPayload), null, 2));
    } catch (e) {
      updateField('toolOutput', '⚠️ Invalid JWT Token. Cannot decode payload.');
    }
  };

  // --- TOOLS: JSON & CSV Transformer ---
  const handleFormatJSON = () => {
    if (!newSnippet.content) return;
    try {
      const parsed = JSON.parse(newSnippet.content);
      const formatted = JSON.stringify(parsed, null, 2);
      updateField('content', formatted);
      updateField('toolOutput', '✅ JSON Formatted Successfully!');
    } catch (e) {
      updateField('toolOutput', '⚠️ Invalid JSON. Cannot format.');
    }
  };

  const handleConvertToCSV = async () => {
    if (!newSnippet.content) return;
    try {
      const parsed = JSON.parse(newSnippet.content);
      let arr = Array.isArray(parsed) ? parsed : [parsed];
      if (arr.length === 0) throw new Error("Empty array");

      const headers = Array.from(new Set(arr.flatMap(obj => Object.keys(obj))));

      const csvRows = [
        headers.join(','),
        ...arr.map(row => headers.map(fieldName => {
          let val = row[fieldName] === null || row[fieldName] === undefined ? '' : row[fieldName];
          let valStr = String(val);
          if (valStr.includes(',') || valStr.includes('"') || valStr.includes('\n')) {
            valStr = `"${valStr.replace(/"/g, '""')}"`;
          }
          return valStr;
        }).join(','))
      ];

      await navigator.clipboard.writeText(csvRows.join('\n'));
      updateField('toolOutput', `✅ Successfully copied ${arr.length} rows to clipboard as CSV!`);
    } catch (e) {
      updateField('toolOutput', '⚠️ Cannot convert to CSV. Ensure your JSON is valid and formatted as an array of objects.');
    }
  };

  // --- TOOLS: Boilerplate Generator ---
  const handleGenerateBoilerplate = () => {
    let code = `/**\n * Google Workspace API Microservice\n * Generated locally by SnipIT\n */\n\n`;
    const indent = bpOptions.tryCatch ? `    ` : `  `;

    // 1. Inject Universal Constants if checked
    if (bpOptions.constants) {
      code += `// --- Global Constants ---\n`;
      code += `const SS_ID = "YOUR_SPREADSHEET_ID";\n`;
      code += `const SHEET_NAME = "Sheet1";\n`;
      code += `const FOLDER_ID = "YOUR_DRIVE_FOLDER_ID";\n`;
      code += `const FILE_ID = "YOUR_DRIVE_FILE_ID";\n`;
      code += `const DOC_ID = "YOUR_DOCUMENT_ID";\n`;
      code += `const CALENDAR_ID = "YOUR_CALENDAR_ID@group.calendar.google.com";\n`;
      code += `const APPSHEET_APP_ID = "YOUR_APPSHEET_APP_ID";\n`;
      code += `const APPSHEET_ACCESS_KEY = "YOUR_APPSHEET_ACCESS_KEY";\n`;
      code += `const GEMINI_API_KEY = "YOUR_GEMINI_API_KEY";\n\n`;
    }

    // 2. Add URL Sanitation Helper (Crucial for secure APIs)
    code += `/**\n * Sanitizes URLs to prevent injection/formatting issues\n */\n`;
    code += `function sanitizeUrl(url) {\n  return encodeURI(url.trim().replace(/\\s/g, ''));\n}\n\n`;

    // 3. Add AppSheet API Template
    code += `/**\n * Sample AppSheet API Call Template\n */\n`;
    code += `function callAppSheetApi(action, data) {\n`;
    code += `  const url = sanitizeUrl(\`https://api.appsheet.com/api/v2/apps/\${APPSHEET_APP_ID}/tables/MyTable/Action\`);\n`;
    code += `  const payload = { Action: action, Properties: { Locale: 'en-US' }, Rows: [data] };\n`;
    code += `  return UrlFetchApp.fetch(url, {\n`;
    code += `    method: 'post',\n`;
    code += `    contentType: 'application/json',\n`;
    code += `    headers: { 'ApplicationAccessKey': APPSHEET_ACCESS_KEY },\n`;
    code += `    payload: JSON.stringify(payload)\n`;
    code += `  });\n}\n\n`;

    // 4. Inject Auth Guard
    if (bpOptions.auth) {
      code += `const SCRIPT_TOKEN = "YOUR_SECRET_TOKEN_HERE";\n\n`;
      code += `function authenticate(e) {\n`;
      code += `  const token = e.parameter.token || (e.postData && JSON.parse(e.postData.contents).token);\n`;
      code += `  if (token !== SCRIPT_TOKEN) throw new Error("Unauthorized");\n`;
      code += `}\n\n`;
    }
    // 5. Inject JSON Helper
    if (bpOptions.json) {
      code += `/**\n * Helper: Create standard JSON Response\n */\n`;
      code += `function createJsonResponse(data) {\n`;
      code += `  return ContentService.createTextOutput(JSON.stringify(data))\n`;
      code += `    .setMimeType(ContentService.MimeType.JSON);\n`;
      code += `}\n\n`;
    }

    // 6. Inject Backoff Logic
    if (bpOptions.backoff) {
      code += `/**\n * Helper: Exponential Backoff for UrlFetchApp\n */\n`;
      code += `function fetchWithBackoff(url, options, maxRetries = 5) {\n`;
      code += `  for (let i = 0; i < maxRetries; i++) {\n`;
      code += `    try {\n`;
      code += `      return UrlFetchApp.fetch(url, options);\n`;
      code += `    } catch (e) {\n`;
      code += `      if (i === maxRetries - 1) throw e;\n`;
      code += `      Utilities.sleep((Math.pow(2, i) * 1000) + Math.round(Math.random() * 1000));\n`;
      code += `    }\n`;
      code += `  }\n`;
      code += `}\n\n`;
    }

    updateField('content', code.trim());
  };

  // --- EXTRACT LOGIC ---
  const extract = async () => {
    try {
      const [tab] = await chrome.tabs.query({ active: true, lastFocusedWindow: true });
      if (!tab || !tab.id) return alert("Could not find an active web page!");

      chrome.tabs.sendMessage(tab.id, { type: "GET_SELECTION" }, (res) => {
        if (chrome.runtime.lastError) {
          if (newSnippet.vault === 'links') {
            setNewSnippet(prev => {
              const updated = { ...prev };
              if (!prev.url && tab.url) updated.url = tab.url;
              if (!prev.title && tab.title) updated.title = tab.title;
              return updated;
            });
            return alert("URL Grabbed! \n\nHowever, to extract highlighted text, please refresh the web page you are trying to extract from.");
          }
          return alert("Action Failed: Please refresh the web page you are trying to extract from!");
        }

        setNewSnippet(prev => {
          const updated = { ...prev };

          if (prev.vault === 'links') {
            const pageUrl = res?.url || tab.url;
            const pageTitle = res?.title || tab.title;
            if (!prev.url && pageUrl) updated.url = pageUrl;
            if (!prev.title && pageTitle) updated.title = pageTitle;
          }

          if (res?.text) {
            if (prev.type === 'Gem') {
              updated.gemContext = (prev.gemContext + '\n\n' + res.text).trim();
            } else if (prev.type === 'Context') {
              updated.content = (prev.content + '\n\n' + res.text).trim();
            } else if (prev.vault === 'links') {
              const currentNotes = prev.content || '';
              updated.content = currentNotes ? (currentNotes + '\n\n' + res.text).trim() : res.text.trim();
            } else if (prev.vault === 'tools') {
              if (prev.type === 'JWT Decoder') handleDecodeJWT(res.text);
              else updated.content = res.text.trim();
              return prev.type === 'JWT Decoder' ? prev : updated;
            } else {
              updated.content = ['AppSheet', 'Sheets'].includes(prev.type) ? minifyExpression(res.text) : res.text;
            }
          } else if (prev.vault !== 'links') {
            alert("No text is currently highlighted on the page.");
          }

          return updated;
        });
      });
    } catch (err) {
      console.error("Extraction error:", err);
    }
  };

  const processCode = () => {
    const processed = formatCodeBlock(newSnippet.content, newSnippet.type);
    updateField('content', processed);
  };

  const handleTagInput = (e) => {
    if (e.key === 'Enter' || e.key === ',') {
      e.preventDefault();
      let cleanedTag = tagInput.trim().toLowerCase().replace(/[^a-z0-9]/g, '');
      if (cleanedTag) {
        cleanedTag = '#' + cleanedTag;
        const currentTags = newSnippet.tags || [];
        if (!currentTags.includes(cleanedTag)) updateField('tags', [...currentTags, cleanedTag]);
      }
      setTagInput('');
    }
  };

  const removeTag = (tagToRemove) => updateField('tags', newSnippet.tags.filter(t => t !== tagToRemove));

  const renderFields = () => {
    // --- TOOLS VAULT ROUTER ---
    if (newSnippet.vault === 'tools') {

      // 1. JWT Decoder Tool
      if (newSnippet.type === 'JWT Decoder') {
        return (
          <div className="flex flex-col gap-3 grow overflow-y-auto custom-scrollbar pr-1 pb-2">
            <div className="flex flex-col gap-1 shrink-0">
              <label className={`text-[10px] font-bold ${theme.textPrimary} uppercase tracking-wider`}>Raw Encoded Token (JWT)</label>
              <textarea
                className="w-full bg-black p-2 font-mono text-[10px] rounded outline-none border border-amber-900/50 text-gray-400 break-all resize-y min-h-20"
                value={newSnippet.content} onChange={e => handleDecodeJWT(e.target.value)} placeholder="eyJhYmd..." spellCheck="false"
              />
            </div>
            <div className="flex flex-col gap-1 grow min-h-37.5">
              <label className={`text-[10px] font-bold ${theme.textPrimary} uppercase tracking-wider`}>Decoded JSON Payload</label>
              <textarea
                readOnly
                className={`w-full grow bg-surface p-3 font-mono text-xs rounded outline-none border ${newSnippet.toolOutput?.includes('⚠️') ? 'border-red-900 text-red-400' : 'border-gray-800 text-emerald-400'} resize-y custom-scrollbar`}
                value={newSnippet.toolOutput} placeholder="Payload will appear here..." spellCheck="false"
              />
            </div>
          </div>
        );
      }

      // 2. JSON Formatter Tool
      if (newSnippet.type === 'JSON Formatter') {
        return (
          <div className="flex flex-col gap-3 grow overflow-y-auto custom-scrollbar pr-1 pb-2">
            <div className="flex flex-col gap-1 grow shrink-0">
              <label className={`text-[10px] font-bold ${theme.textPrimary} uppercase tracking-wider`}>Raw JSON Input</label>
              <textarea
                className="w-full grow bg-black p-3 font-mono text-xs rounded outline-none border border-amber-900/50 text-gray-300 resize-y min-h-45 custom-scrollbar"
                value={newSnippet.content} onChange={e => { updateField('content', e.target.value); updateField('toolOutput', ''); }}
                placeholder='[{"name": "Admin", "email": "admin@workspace.com"}]' spellCheck="false"
              />
            </div>
            <div className="flex gap-2 shrink-0">
              <button onClick={handleFormatJSON} className="flex-1 bg-surface hover:bg-gray-800 text-amber-400 border border-amber-900/50 text-[11px] py-2 rounded transition font-bold tracking-wide">
                {'{ }'} Format JSON
              </button>
              <button onClick={handleConvertToCSV} className="flex-1 bg-surface hover:bg-gray-800 text-emerald-400 border border-emerald-900/50 text-[11px] py-2 rounded transition font-bold tracking-wide">
                📊 Copy as CSV
              </button>
            </div>
            {newSnippet.toolOutput && (
              <div className={`p-2 rounded text-[10px] font-bold text-center border ${newSnippet.toolOutput.includes('⚠️') ? 'bg-red-900/20 text-red-400 border-red-900/50' : 'bg-emerald-900/20 text-emerald-400 border-emerald-900/50'} shrink-0`}>
                {newSnippet.toolOutput}
              </div>
            )}
          </div>
        );
      }

      // 3. Boilerplate Generator Tool
      if (newSnippet.type === 'Boilerplate Gen') {
        return (
          <div className="flex flex-col gap-3 grow overflow-y-auto custom-scrollbar pr-1 pb-2">
            <div className="flex flex-col gap-2 shrink-0 bg-black p-3 rounded border border-amber-900/50">
              <label className={`text-[10px] font-bold ${theme.textPrimary} uppercase tracking-wider mb-1`}>Generator Options</label>
              <div className="grid grid-cols-2 gap-2 text-[10px] text-gray-300">
                <label className="flex items-center gap-2 cursor-pointer hover:text-white">
                  <input type="checkbox" checked={bpOptions.constants} onChange={e => setBpOptions({ ...bpOptions, constants: e.target.checked })} className="accent-amber-500" />
                  Workspace Constants
                </label>
                <label className="flex items-center gap-2 cursor-pointer hover:text-white">
                  <input type="checkbox" checked={bpOptions.auth} onChange={e => setBpOptions({ ...bpOptions, auth: e.target.checked })} className="accent-amber-500" />
                  Require Auth Token
                </label>
                <label className="flex items-center gap-2 cursor-pointer hover:text-white">
                  <input type="checkbox" checked={bpOptions.doGet} onChange={e => setBpOptions({ ...bpOptions, doGet: e.target.checked })} className="accent-amber-500" />
                  Include doGet(e)
                </label>
                <label className="flex items-center gap-2 cursor-pointer hover:text-white">
                  <input type="checkbox" checked={bpOptions.doPost} onChange={e => setBpOptions({ ...bpOptions, doPost: e.target.checked })} className="accent-amber-500" />
                  Include doPost(e)
                </label>
                <label className="flex items-center gap-2 cursor-pointer hover:text-white">
                  <input type="checkbox" checked={bpOptions.tryCatch} onChange={e => setBpOptions({ ...bpOptions, tryCatch: e.target.checked })} className="accent-amber-500" />
                  Include Try / Catch
                </label>
                <label className="flex items-center gap-2 cursor-pointer hover:text-white">
                  <input type="checkbox" checked={bpOptions.json} onChange={e => setBpOptions({ ...bpOptions, json: e.target.checked })} className="accent-amber-500" />
                  Return JSON Output
                </label>
                <label className="flex items-center gap-2 cursor-pointer hover:text-white col-span-2">
                  <input type="checkbox" checked={bpOptions.backoff} onChange={e => setBpOptions({ ...bpOptions, backoff: e.target.checked })} className="accent-amber-500" />
                  UrlFetchApp Backoff
                </label>
              </div>
              <button onClick={handleGenerateBoilerplate} className="mt-3 w-full bg-surface hover:bg-gray-800 text-amber-400 border border-amber-900/50 text-[11px] py-1.5 rounded transition font-bold tracking-wide flex justify-center items-center gap-1">
                ⚡ Generate Script
              </button>
            </div>

            <div className="flex flex-col gap-1 grow min-h-37.5">
              <label className={`text-[10px] font-bold ${theme.textPrimary} uppercase tracking-wider`}>Generated Apps Script</label>
              <textarea
                className="w-full grow bg-surface p-3 font-mono text-xs rounded outline-none border border-gray-800 text-gray-300 resize-y custom-scrollbar"
                value={newSnippet.content} onChange={e => updateField('content', e.target.value)} placeholder="// Click generate to build script..." spellCheck="false"
              />
            </div>
          </div>
        );
      }

      // 4. Regex Tester Tool
      if (newSnippet.type === 'Regex Tester') {
        const regex = newSnippet.pattern || '';
        let matches = [];
        try {
          if (regex) {
            // eslint-disable-next-line
            // securecoder-ignore
            const re = new RegExp(regex, 'g');
            matches = [...(newSnippet.content || '').matchAll(re)];
          }
        } catch (e) {}

        return (
          <div className="flex flex-col gap-3 grow overflow-y-auto custom-scrollbar pr-1 pb-2">
            <div className="flex flex-col gap-1 shrink-0">
              <label className={`text-[10px] font-bold ${theme.textPrimary} uppercase tracking-wider`}>Input Text</label>
              <textarea
                className="w-full bg-black p-2 font-mono text-[10px] rounded outline-none border border-gray-800 text-gray-300 resize-y min-h-20"
                value={newSnippet.content} onChange={e => updateField('content', e.target.value)} placeholder="Paste text to test against..."
              />
            </div>

            <div className="flex flex-col gap-1 shrink-0">
              <label className={`text-[10px] font-bold ${theme.textPrimary} uppercase tracking-wider`}>Regex Pattern</label>
              <div className="flex gap-2">
                <input
                  className="grow bg-surface p-2 font-mono text-xs rounded outline-none border border-amber-900/50 text-amber-400"
                  value={regex} onChange={e => updateField('pattern', e.target.value)} placeholder="e.g. [a-z0-9._%+-]+@[a-z0-9.-]+\.[a-z]{2,4}"
                />
                <select 
                  className="bg-surface p-2 rounded text-[10px] outline-none border border-amber-900/50 text-amber-400 w-1/3" 
                  value={newSnippet.outputFormat || 'None'} 
                  onChange={e => updateField('outputFormat', e.target.value)}
                >
                  <option value="None">No Wrapper</option>
                  <option value="Literal">Literal (/.../g)</option>
                  <option value="String">String ("...")</option>
                  <option value="RegExp">Constructor (new RegExp)</option>
                </select>
              </div>
            </div>

            <div className="flex flex-col gap-1 grow min-h-25">
              <label className={`text-[10px] font-bold ${theme.textPrimary} uppercase tracking-wider`}>Results ({matches.length} matches)</label>
              <div className="w-full grow bg-black p-3 font-mono text-xs rounded border border-gray-800 text-emerald-400 overflow-y-auto custom-scrollbar">
                {matches.length > 0 ? (
                  matches.map((m, i) => <div key={i} className="border-b border-gray-900 py-1">Match {i + 1}: <span className="text-white">{m[0]}</span></div>)
                ) : <span className="text-gray-600 italic">No matches found.</span>}
              </div>
            </div>
          </div>
        );
      }

      // 5. Base64 & URL Converter
      if (newSnippet.type === 'Base64 & URL') {
        const handleEncode64 = () => { try { updateField('toolOutput', btoa(newSnippet.content)); } catch(e) { updateField('toolOutput', 'Error: ' + e.message); } };
        const handleDecode64 = () => { try { updateField('toolOutput', atob(newSnippet.content)); } catch(e) { updateField('toolOutput', 'Error: ' + e.message); } };
        const handleEncodeUrl = () => { try { updateField('toolOutput', encodeURIComponent(newSnippet.content)); } catch(e) { updateField('toolOutput', 'Error: ' + e.message); } };
        const handleDecodeUrl = () => { try { updateField('toolOutput', decodeURIComponent(newSnippet.content)); } catch(e) { updateField('toolOutput', 'Error: ' + e.message); } };

        return (
          <div className="flex flex-col gap-3 grow overflow-y-auto custom-scrollbar pr-1 pb-2">
            <div className="flex flex-col gap-1 shrink-0">
              <label className={`text-[10px] font-bold ${theme.textPrimary} uppercase tracking-wider`}>Input String</label>
              <textarea
                className="w-full bg-black p-2 font-mono text-[10px] rounded outline-none border border-amber-900/50 text-gray-300 resize-y min-h-20"
                value={newSnippet.content || ''} onChange={e => { updateField('content', e.target.value); updateField('toolOutput', ''); }} placeholder="String to convert..." spellCheck="false"
              />
            </div>
            <div className="grid grid-cols-4 gap-2 shrink-0">
              <button onClick={handleEncode64} className="bg-surface hover:bg-gray-800 text-amber-400 border border-amber-900/50 text-[10px] py-1.5 rounded transition font-bold tracking-wide">Base64 Encode</button>
              <button onClick={handleDecode64} className="bg-surface hover:bg-gray-800 text-emerald-400 border border-emerald-900/50 text-[10px] py-1.5 rounded transition font-bold tracking-wide">Base64 Decode</button>
              <button onClick={handleEncodeUrl} className="bg-surface hover:bg-gray-800 text-amber-400 border border-amber-900/50 text-[10px] py-1.5 rounded transition font-bold tracking-wide">URL Encode</button>
              <button onClick={handleDecodeUrl} className="bg-surface hover:bg-gray-800 text-emerald-400 border border-emerald-900/50 text-[10px] py-1.5 rounded transition font-bold tracking-wide">URL Decode</button>
            </div>
            <div className="flex flex-col gap-1 grow min-h-25">
              <label className={`text-[10px] font-bold ${theme.textPrimary} uppercase tracking-wider`}>Output</label>
              <textarea
                readOnly
                className="w-full grow bg-surface p-3 font-mono text-[10px] rounded outline-none border border-gray-800 text-emerald-400 resize-y custom-scrollbar break-all"
                value={newSnippet.toolOutput || ''} placeholder="Result will appear here..." spellCheck="false"
              />
            </div>
          </div>
        );
      }

      // 6. Color Converter
      if (newSnippet.type === 'Color Converter') {
        const handleColorChange = (val) => {
            updateField('content', val);
            updateField('toolOutput', convertColor(val));
        };

        // Extract a valid hex to drive the UI color picker and visual preview
        let parsedHex = '#000000';
        if (newSnippet.toolOutput && newSnippet.toolOutput.includes('HEX: ')) {
           const match = newSnippet.toolOutput.match(/HEX:\s*(#[0-9a-fA-F]{6})/i);
           if (match) parsedHex = match[1];
        }

        return (
          <div className="flex flex-col gap-3 grow overflow-y-auto custom-scrollbar pr-1 pb-2">
            <div className="flex flex-col gap-2 shrink-0 bg-black p-3 rounded border border-amber-900/50">
              <label className={`text-[10px] font-bold ${theme.textPrimary} uppercase tracking-wider`}>Select or Type Color</label>
              <div className="flex gap-2 items-center">
                <div className="relative w-12 h-10 rounded overflow-hidden border border-gray-800 shadow-sm cursor-pointer shrink-0">
                  <input
                    type="color"
                    className="absolute -top-2 -left-2 w-16 h-16 cursor-pointer border-0 p-0"
                    value={parsedHex}
                    onChange={(e) => handleColorChange(e.target.value)}
                    title="Choose a visual color"
                  />
                </div>
                <input
                  className="grow bg-surface p-2 font-mono text-xs rounded outline-none border border-gray-800 text-gray-300"
                  value={newSnippet.content || ''} onChange={(e) => handleColorChange(e.target.value)} placeholder="#ff5500 or rgb(255, 85, 0)" spellCheck="false"
                />
              </div>
            </div>
            
            <div className="flex gap-3 grow min-h-25">
              <div className="flex flex-col gap-1 flex-1">
                <label className={`text-[10px] font-bold ${theme.textPrimary} uppercase tracking-wider`}>Converted Formats</label>
                <textarea
                  readOnly
                  className="w-full grow bg-surface p-3 font-mono text-xs rounded outline-none border border-gray-800 text-emerald-400 resize-none custom-scrollbar leading-loose"
                  value={newSnippet.toolOutput || ''} placeholder="Results..." spellCheck="false"
                />
              </div>
              <div className="flex flex-col gap-1 w-32 shrink-0">
                <label className={`text-[10px] font-bold ${theme.textPrimary} uppercase tracking-wider`}>Visual Preview</label>
                <div 
                  className="w-full grow rounded border border-gray-800 shadow-inner flex items-center justify-center text-[10px] font-bold transition-colors duration-200"
                  style={{ backgroundColor: parsedHex, color: parseInt(parsedHex.replace('#', ''), 16) > 0xffffff / 2 ? '#000000' : '#ffffff' }}
                >
                  {parsedHex.toUpperCase()}
                </div>
              </div>
            </div>
          </div>
        );
      }

      // 7. Hash Generator
      if (newSnippet.type === 'Hash Generator') {
        const handleHashChange = async (e) => {
            const val = e.target.value;
            updateField('content', val);
            if (!val) return updateField('toolOutput', '');
            try {
                const hashes = await generateHashes(val);
                updateField('toolOutput', `SHA-1: ${hashes.sha1}\n\nSHA-256: ${hashes.sha256}`);
            } catch (err) {
                updateField('toolOutput', 'Error generating hashes.');
            }
        };
        return (
          <div className="flex flex-col gap-3 grow overflow-y-auto custom-scrollbar pr-1 pb-2">
            <div className="flex flex-col gap-1 shrink-0">
              <label className={`text-[10px] font-bold ${theme.textPrimary} uppercase tracking-wider`}>String to Hash</label>
              <textarea
                className="w-full bg-black p-2 font-mono text-[10px] rounded outline-none border border-amber-900/50 text-gray-300 resize-y min-h-20"
                value={newSnippet.content || ''} onChange={handleHashChange} placeholder="Enter text..." spellCheck="false"
              />
            </div>
            <div className="flex flex-col gap-1 grow min-h-25">
              <label className={`text-[10px] font-bold ${theme.textPrimary} uppercase tracking-wider`}>Hashes (SHA-1, SHA-256)</label>
              <textarea
                readOnly
                className="w-full grow bg-surface p-3 font-mono text-[10px] rounded outline-none border border-gray-800 text-emerald-400 resize-y custom-scrollbar break-all"
                value={newSnippet.toolOutput || ''} placeholder="Hashes will appear here..." spellCheck="false"
              />
            </div>
          </div>
        );
      }

      // 8. Mock Data Gen
      if (newSnippet.type === 'Mock Data Gen') {
        const availableFields = ['id', 'name', 'email', 'department', 'role', 'address', 'phone', 'createdAt'];
        
        const toggleField = (field) => {
          setMockOptions(prev => {
             const fields = prev.fields.includes(field) ? prev.fields.filter(f => f !== field) : [...prev.fields, field];
             return { ...prev, fields };
          });
        };

        const handleGenerateMockData = () => {
          const raw = generateMockUsers(mockOptions.count, mockOptions.fields);
          setMockDataRaw(raw);
          let formatted = '';
          if (mockOptions.format === 'JSON') formatted = formatJSON(raw, mockOptions.jsonStyle);
          else if (mockOptions.format === 'CSV') formatted = formatCSV(raw, mockOptions.csvDelimiter);
          else if (mockOptions.format === 'SQL') formatted = formatSQL(raw, mockOptions.sqlDialect);
          else if (mockOptions.format === 'Plain Text') formatted = formatPlainText(raw);
          updateField('toolOutput', formatted);
        };

        const copyMockData = () => {
          if (!newSnippet.toolOutput) return;
          navigator.clipboard.writeText(newSnippet.toolOutput).then(() => alert("Copied to clipboard!"));
        };

        return (
          <div className="flex flex-col gap-3 grow overflow-y-auto custom-scrollbar pr-1 pb-2">
            <div className="flex flex-col gap-2 shrink-0 bg-black p-3 rounded border border-amber-900/50">
              <div className="flex justify-between items-center mb-1">
                <label className={`text-[10px] font-bold ${theme.textPrimary} uppercase tracking-wider`}>Data Config</label>
                <div className="flex items-center gap-2">
                  <label className="text-[10px] text-gray-400">Count:</label>
                  <input type="number" min="1" max="1000" className="bg-surface text-amber-400 text-xs px-2 py-1 rounded w-16 border border-gray-800 outline-none" value={mockOptions.count} onChange={e => setMockOptions({...mockOptions, count: parseInt(e.target.value) || 1})} />
                </div>
              </div>
              <div className="grid grid-cols-3 gap-2 text-[10px] text-gray-300 mb-2 border-b border-gray-800 pb-2">
                {availableFields.map(f => (
                  <label key={f} className="flex items-center gap-2 cursor-pointer hover:text-white capitalize">
                    <input type="checkbox" checked={mockOptions.fields.includes(f)} onChange={() => toggleField(f)} className="accent-amber-500" />
                    {f}
                  </label>
                ))}
              </div>
              
              <div className="flex flex-col gap-2 mb-2">
                <div className="flex justify-between items-center">
                  <div className="flex gap-2">
                    <select className="bg-surface text-[10px] text-amber-400 border border-gray-800 rounded p-1 outline-none" value={mockOptions.format} onChange={e => setMockOptions({...mockOptions, format: e.target.value})}>
                      <option value="JSON">JSON</option>
                      <option value="CSV">CSV</option>
                      <option value="SQL">SQL</option>
                      <option value="Plain Text">Plain Text</option>
                    </select>

                    {mockOptions.format === 'JSON' && (
                      <select className="bg-surface text-[10px] text-gray-300 border border-gray-800 rounded p-1 outline-none" value={mockOptions.jsonStyle} onChange={e => setMockOptions({...mockOptions, jsonStyle: e.target.value})}>
                        <option value="Prettified">Prettified</option>
                        <option value="Compressed">Compressed</option>
                      </select>
                    )}
                    {mockOptions.format === 'CSV' && (
                      <select className="bg-surface text-[10px] text-gray-300 border border-gray-800 rounded p-1 outline-none" value={mockOptions.csvDelimiter} onChange={e => setMockOptions({...mockOptions, csvDelimiter: e.target.value})}>
                        <option value="\n">LF (\n)</option>
                        <option value="\r\n">CRLF (\r\n)</option>
                      </select>
                    )}
                    {mockOptions.format === 'SQL' && (
                      <select className="bg-surface text-[10px] text-gray-300 border border-gray-800 rounded p-1 outline-none" value={mockOptions.sqlDialect} onChange={e => setMockOptions({...mockOptions, sqlDialect: e.target.value})}>
                        <option value="Postgres">Postgres</option>
                        <option value="BigQuery">BigQuery</option>
                        <option value="Cloud SQL (MySQL)">Cloud SQL (MySQL)</option>
                      </select>
                    )}
                  </div>
                  {(mockOptions.format === 'JSON' || mockOptions.format === 'CSV') && (
                    <label className="flex items-center gap-1 text-[10px] text-gray-300 cursor-pointer">
                      <input type="checkbox" checked={mockOptions.previewAsTable} onChange={e => setMockOptions({...mockOptions, previewAsTable: e.target.checked})} className="accent-amber-500" />
                      Preview as Table
                    </label>
                  )}
                </div>
              </div>

              <div className="flex gap-2">
                <button onClick={handleGenerateMockData} className="flex-1 bg-surface hover:bg-gray-800 text-amber-400 border border-amber-900/50 text-[10px] py-1.5 rounded transition font-bold tracking-wide">⚡ Generate & Format</button>
                <button onClick={() => updateField('toolOutput', generateLoremIpsum(mockOptions.count))} className="flex-1 bg-surface hover:bg-gray-800 text-emerald-400 border border-emerald-900/50 text-[10px] py-1.5 rounded transition font-bold tracking-wide">📝 Generate Lorem</button>
              </div>
            </div>

            <div className="flex flex-col gap-1 grow min-h-25">
              <div className="flex justify-between items-center">
                <label className={`text-[10px] font-bold ${theme.textPrimary} uppercase tracking-wider`}>Generated Data</label>
                <button onClick={copyMockData} className="bg-emerald-900/30 text-emerald-400 hover:bg-emerald-900/60 px-3 py-1 rounded text-[10px] font-bold transition flex items-center gap-1 border border-emerald-900/50">
                  <Clipboard size={10} /> Copy Output
                </button>
              </div>

              {mockOptions.previewAsTable && mockDataRaw && mockDataRaw.length > 0 && (mockOptions.format === 'JSON' || mockOptions.format === 'CSV') ? (
                <div className="w-full grow bg-surface p-2 rounded border border-gray-800 overflow-auto custom-scrollbar">
                  <table className="w-full text-left text-[10px] text-gray-300 border-collapse">
                    <thead>
                      <tr>
                        {Object.keys(mockDataRaw[0]).map(key => (
                          <th key={key} className="border border-gray-700 bg-border px-2 py-1 font-bold capitalize">{key}</th>
                        ))}
                      </tr>
                    </thead>
                    <tbody>
                      {mockDataRaw.map((row, i) => (
                        <tr key={i} className="hover:bg-[#252525]">
                          {Object.values(row).map((val, j) => (
                            <td key={j} className="border border-gray-700 px-2 py-1 truncate max-w-37.5">{String(val)}</td>
                          ))}
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              ) : (
                <textarea
                  readOnly
                  className="w-full grow bg-surface p-3 font-mono text-[10px] rounded outline-none border border-gray-800 text-gray-300 resize-y custom-scrollbar"
                  value={newSnippet.toolOutput || ''} placeholder="Data will appear here..." spellCheck="false"
                />
              )}
            </div>
          </div>
        );
      }

      // 9. Case Converter
      if (newSnippet.type === 'Case Converter') {
        const handleCaseChange = (e) => {
            const val = e.target.value;
            updateField('content', val);
            if (!val) return updateField('toolOutput', '');
            const cases = `camelCase: ${toCamelCase(val)}\nsnake_case: ${toSnakeCase(val)}\nPascalCase: ${toPascalCase(val)}\nkebab-case: ${toKebabCase(val)}\nCONSTANT_CASE: ${toConstantCase(val)}`;
            updateField('toolOutput', cases);
        };
        return (
          <div className="flex flex-col gap-3 grow overflow-y-auto custom-scrollbar pr-1 pb-2">
            <div className="flex flex-col gap-1 shrink-0">
              <label className={`text-[10px] font-bold ${theme.textPrimary} uppercase tracking-wider`}>Input String</label>
              <input
                className="w-full bg-black p-2 font-mono text-[10px] rounded outline-none border border-amber-900/50 text-gray-300"
                value={newSnippet.content || ''} onChange={handleCaseChange} placeholder="my variable name" spellCheck="false"
              />
            </div>
            <div className="flex flex-col gap-1 grow min-h-25">
              <label className={`text-[10px] font-bold ${theme.textPrimary} uppercase tracking-wider`}>Converted Cases</label>
              <textarea
                readOnly
                className="w-full grow bg-surface p-3 font-mono text-[10px] rounded outline-none border border-gray-800 text-emerald-400 resize-y custom-scrollbar whitespace-pre-wrap"
                value={newSnippet.toolOutput || ''} placeholder="Results..." spellCheck="false"
              />
            </div>
          </div>
        );
      }

      // 10. Markdown Pad
      if (newSnippet.type === 'Markdown Pad') {
        const handleMarkdownChange = async (e) => {
            const val = e.target.value;
            updateField('content', val);
            const html = await marked.parse(val);
            updateField('toolOutput', html);
        };
        return (
          <div className="flex flex-col gap-2 grow overflow-y-auto custom-scrollbar pr-1 pb-2 min-h-75">
            {!showMarkdownPreview ? (
              <div className="flex flex-col gap-1 flex-1">
                <div className="flex justify-between items-center">
                  <label className={`text-[10px] font-bold ${theme.textPrimary} uppercase tracking-wider`}>Raw Markdown</label>
                  <button onClick={() => setShowMarkdownPreview(true)} className={`text-[10px] px-2 py-1 rounded ${theme.bgPrimary} ${theme.bgHover} text-white font-bold transition-colors`}>Show Preview</button>
                </div>
                <textarea
                  className="w-full grow min-h-62.5 bg-black p-3 font-mono text-[11px] rounded outline-none border border-amber-900/50 text-gray-300 resize-y custom-scrollbar"
                  value={newSnippet.content || ''} onChange={handleMarkdownChange} placeholder="# Hello World..." spellCheck="false"
                />
              </div>
            ) : (
              <div className="flex flex-col gap-1 flex-1">
                <div className="flex justify-between items-center">
                  <label className={`text-[10px] font-bold ${theme.textPrimary} uppercase tracking-wider`}>Preview</label>
                  <button onClick={() => setShowMarkdownPreview(false)} className={`text-[10px] px-2 py-1 rounded bg-gray-700 hover:bg-gray-600 text-white font-bold transition-colors`}>Back to Editor</button>
                </div>
                <div
                  className="w-full grow min-h-62.5 bg-surface p-4 text-sm rounded outline-none border border-gray-800 text-gray-300 overflow-y-auto custom-scrollbar prose prose-invert prose-sm"
                  dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(newSnippet.toolOutput || '') }}
                />
              </div>
            )}
          </div>
        );
      }

      return <div className="text-gray-500 text-sm p-4 text-center border border-gray-800 border-dashed rounded mt-4">Tool under construction.</div>;
    }

    // --- LINKS VAULT ---
    if (newSnippet.vault === 'links') {
      return (
        <div className="flex flex-col gap-3 grow overflow-y-auto custom-scrollbar pr-1 pb-2">
          <div className="flex flex-col gap-1 shrink-0">
            <label htmlFor="link-target-url" className={`text-[10px] font-bold ${theme.textPrimary} uppercase tracking-wider`}>Target URL</label>
            <input
              id="link-target-url" name="linkTargetUrl"
              className="w-full bg-black p-2 font-sans text-xs rounded outline-none border border-gray-800 text-gray-300 placeholder-gray-600"
              value={newSnippet.url || ''} onChange={e => updateField('url', e.target.value)} placeholder="https://..."
            />
          </div>
          <div className="flex flex-col gap-1 grow min-h-25">
            <label htmlFor="link-notes" className={`text-[10px] font-bold ${theme.textPrimary} uppercase tracking-wider`}>Notes / cURL Command</label>
            <textarea
              id="link-notes" name="linkNotes"
              className="w-full grow bg-black p-3 font-mono text-xs rounded outline-none border border-gray-800 resize-y custom-scrollbar min-h-25"
              value={newSnippet.content} onChange={e => updateField('content', e.target.value)} placeholder="Optional description or command..." spellCheck="false"
            />
          </div>
        </div>
      );
    }

    // --- DEV & AI VAULTS ---
    switch (newSnippet.type) {
      case 'Gem':
        return (
          <div className="grow flex flex-col gap-3 overflow-y-auto custom-scrollbar pr-1 pb-2">
            <GemField id="gem-premise" label="Premise" value={newSnippet.gemPremise} onChange={(v) => updateField('gemPremise', v)} theme={theme} placeholder="Overall goal or role..." />
            <GemField id="gem-persona" label="Persona / Instructions" value={newSnippet.gemPersona} onChange={(v) => updateField('gemPersona', v)} theme={theme} placeholder="Step-by-step instructions..." />
            <GemField id="gem-context" label="Context / Examples" value={newSnippet.gemContext} onChange={(v) => updateField('gemContext', v)} theme={theme} placeholder="Background data..." />
            <GemField id="gem-rules" label="Rules" value={newSnippet.gemRules} onChange={(v) => updateField('gemRules', v)} theme={theme} placeholder="Strict constraints..." />
          </div>
        );

      case 'Prompt':
        return (
          <div className="flex flex-col gap-3 grow">
            <label htmlFor="prompt-content" className="sr-only">Prompt Instruction</label>
            <textarea
              id="prompt-content" name="promptContent"
              className="grow min-h-37.5 bg-black p-3 font-sans text-sm rounded outline-none border border-gray-800 resize-y custom-scrollbar"
              value={newSnippet.content} onChange={e => updateField('content', e.target.value)} placeholder="Enter your zero-shot instruction here..."
            />
            <div className="grid grid-cols-2 gap-2 shrink-0">
              <label htmlFor="prompt-format" className="sr-only">Output Format</label>
              <select id="prompt-format" name="promptFormat" className="bg-surface p-2 rounded text-xs border border-gray-800 text-white outline-none" value={newSnippet.outputFormat} onChange={e => updateField('outputFormat', e.target.value)}>
                <option>Markdown Table</option><option>JSON Object</option><option>Bullet Points</option><option>Technical Essay</option>
              </select>

              <label htmlFor="prompt-tone" className="sr-only">Tone</label>
              <select id="prompt-tone" name="promptTone" className="bg-surface p-2 rounded text-xs border border-gray-800 text-white outline-none" value={newSnippet.tone} onChange={e => updateField('tone', e.target.value)}>
                <option>Professional</option><option>Creative</option><option>Concise</option><option>Academic</option>
              </select>
            </div>
          </div>
        );

      case 'System Prompt':
        return (
          <div className="flex flex-col gap-3 grow">
            <label htmlFor="sys-prompt-content" className="sr-only">System Rules</label>
            <textarea
              id="sys-prompt-content" name="sysPromptContent"
              className="grow min-h-37.5 bg-black p-3 font-mono text-xs rounded border border-red-900/30 outline-none resize-y custom-scrollbar"
              value={newSnippet.content} onChange={e => updateField('content', e.target.value)} placeholder="Define the global behavioral guardrails..."
            />
            <div className="flex items-center gap-4 bg-surface p-2 rounded border border-gray-800 shrink-0">
              <label htmlFor="sys-temp" className="text-[10px] uppercase font-bold text-gray-500 whitespace-nowrap">Temperature ({newSnippet.temp})</label>
              <input id="sys-temp" name="sysTemp" type="range" min="0" max="1" step="0.1" className="grow" value={newSnippet.temp} onChange={e => updateField('temp', e.target.value)} />
            </div>
          </div>
        );

      case 'Few-Shot':
        return (
          <div className="flex flex-col gap-2 grow overflow-y-auto custom-scrollbar pr-1">
            <label htmlFor="fs-pattern" className={`text-[10px] font-bold ${theme.textPrimary} uppercase`}>Pattern Template</label>
            <input id="fs-pattern" name="fsPattern" className="bg-surface p-2 rounded text-xs border border-gray-800 outline-none text-white shrink-0" placeholder="Input: {{text}} -> Output: {{result}}" value={newSnippet.pattern} onChange={e => updateField('pattern', e.target.value)} />

            <label htmlFor="fs-content" className="sr-only">Examples</label>
            <textarea
              id="fs-content" name="fsContent"
              className="grow min-h-37.5 bg-black p-3 font-mono text-xs rounded border border-gray-800 outline-none resize-y custom-scrollbar"
              value={newSnippet.content} onChange={e => updateField('content', e.target.value)} placeholder="Paste example pairs here..."
            />
          </div>
        );

      case 'Context':
        return (
          <div className="flex flex-col gap-3 grow">
            <label htmlFor="ctx-source" className="sr-only">Source URL</label>
            <input id="ctx-source" name="ctxSource" className="bg-surface p-2 rounded text-xs border border-gray-800 outline-none text-white shrink-0" placeholder="Source URL or File Reference..." value={newSnippet.source} onChange={e => updateField('source', e.target.value)} />

            <label htmlFor="ctx-content" className="sr-only">Context Data</label>
            <textarea
              id="ctx-content" name="ctxContent"
              className="grow min-h-37.5 bg-black p-3 font-sans text-xs rounded border border-gray-800 outline-none resize-y custom-scrollbar"
              value={newSnippet.content} onChange={e => updateField('content', e.target.value)} placeholder="Paste raw documentation or database content..."
            />
          </div>
        );

      default:
        return (
          <div className="relative grow flex flex-col min-h-37.5">
            <div className="flex justify-between items-center mb-1">
              <label htmlFor="dev-code-content" className="sr-only">Code Snippet</label>
              {(userLicense?.tier === 'enterprise' || userLicense?.tier === 'enterprise_org') && (
                <button 
                  onClick={() => setShowAiGenerator(!showAiGenerator)} 
                  className={`text-[10px] px-2 py-1 rounded flex items-center gap-1 font-bold transition-colors ${showAiGenerator ? 'bg-indigo-600 hover:bg-indigo-500 text-white' : `${theme.bgPrimary} ${theme.bgHover} text-white`}`}
                >
                  <Sparkles size={12} /> {showAiGenerator ? 'Hide AI Generator' : 'Generate with AI'}
                </button>
              )}
            </div>
            {showAiGenerator && (userLicense?.tier === 'enterprise' || userLicense?.tier === 'enterprise_org') && (
              <div className="flex flex-col gap-2 mb-2 p-3 bg-surface rounded border border-indigo-500/30 shadow-inner">
                <textarea 
                  className="w-full bg-black p-2 text-xs rounded border border-gray-800 text-gray-300 outline-none resize-y min-h-15"
                  placeholder="Describe the code you want to generate..."
                  value={aiPrompt}
                  onChange={(e) => setAiPrompt(e.target.value)}
                />
                <button 
                  onClick={async () => {
                    if (!aiPrompt.trim()) return;
                    setIsGenerating(true);
                    try {
                      const result = await generateCode(aiPrompt, newSnippet.type);
                      trackEvent('generate_with_ai', { language: newSnippet.type });
                      // Remove markdown formatting blocks if it wrapped the code
                      const cleanResult = result.replace(/^```[a-z]*\n/m, '').replace(/```$/m, '');
                      updateField('content', cleanResult);
                      setShowAiGenerator(false);
                      setAiPrompt('');
                    } catch (err) {
                      alert("Failed to generate code.");
                    } finally {
                      setIsGenerating(false);
                    }
                  }}
                  disabled={isGenerating}
                  className="w-full bg-indigo-600 hover:bg-indigo-500 disabled:bg-gray-700 text-white text-xs font-bold py-1.5 rounded transition flex items-center justify-center gap-1"
                >
                   {isGenerating ? 'Generating...' : 'Generate Code'}
                </button>
              </div>
            )}
            <textarea
              id="dev-code-content" name="devCodeContent"
              className="w-full grow min-h-37.5 pb-8 bg-[#0d0d0d] p-3 font-mono text-xs rounded outline-none border border-gray-800 resize-y custom-scrollbar"
              value={newSnippet.content} onChange={e => updateField('content', e.target.value)}
              placeholder={`Paste or write code here...`} spellCheck="false"
            />
            <button onClick={processCode} className="absolute bottom-2 right-2 bg-gray-800 hover:bg-gray-700 text-gray-300 text-[10px] px-2 py-1 rounded border border-gray-700 transition">
              {newSnippet.type === 'HTML' ? 'Sanitize' : 'Format'}
            </button>
          </div>
        );
    }
  };

  return (
    <div className="flex flex-col h-full space-y-3">
      <div className="flex justify-between items-center border-b border-gray-800 pb-2 shrink-0">
        <label htmlFor="item-title" className="sr-only">Item Title</label>
        <input
          id="item-title" name="itemTitle"
          className="bg-transparent text-white text-lg outline-none w-full placeholder-gray-600 font-semibold"
          placeholder={newSnippet.type === 'Gem' ? "Gem Title" : (newSnippet.vault === 'dev' ? "Snippet Title" : "Item Title")}
          value={newSnippet.title} onChange={e => updateField('title', e.target.value)} autoComplete="off"
        />
        <button onClick={() => newSnippet.history?.length > 0 ? setShowHistory(!showHistory) : alert("Save an edit first to create a version history!")} className={`ml-2 transition-colors ${newSnippet.history?.length > 0 ? `${theme.textPrimary} hover:text-white` : 'text-gray-700'}`} title="Version History">
          <HistoryIcon size={18} />
        </button>

      </div>

      <div className="flex gap-2 shrink-0">
        <label htmlFor="item-type" className="sr-only">Item Type</label>
        <select id="item-type" name="itemType" className="bg-surface p-2 rounded text-xs outline-none border border-gray-800 text-white w-1/3" value={newSnippet.type} onChange={e => updateField('type', e.target.value)}>
          {availableLangs.map(lang => <option key={lang} value={lang}>{lang}</option>)}
        </select>

        <div className="relative w-2/3">
          <Tag size={12} className="absolute left-2.5 top-2.5 text-gray-500" />
          <label htmlFor="add-tag" className="sr-only">Add Tag</label>
          <input
            id="add-tag" name="addTag"
            className="w-full bg-surface p-2 pl-7 text-xs rounded outline-none border border-gray-800 text-white placeholder-gray-500"
            placeholder="Add tag (Press Enter)..." value={tagInput} onChange={(e) => setTagInput(e.target.value)} onKeyDown={handleTagInput}
          />
        </div>
      </div>

      <div className="flex gap-2 shrink-0">
        <label htmlFor="item-visibility" className="sr-only">Visibility</label>
        {userLicense?.tier !== 'free' && (
          <select id="item-visibility" name="itemVisibility" className="bg-surface p-2 rounded text-xs outline-none border border-gray-800 text-white w-1/3" value={newSnippet.visibility || 'private'} onChange={e => {
              updateField('visibility', e.target.value);
              if (e.target.value === 'public') updateField('isPublic', true);
              else updateField('isPublic', false);
          }}>
            <option value="private">Private</option>
            {organization && <option value="team">Team</option>}
            {organization && <option value="organization">Organization</option>}
            <option value="public">Public</option>
          </select>
        )}
        
        {newSnippet.visibility === 'team' && (
          <select className="bg-surface p-2 rounded text-xs outline-none border border-gray-800 text-white w-2/3" value={newSnippet.teamId || ''} onChange={e => updateField('teamId', e.target.value)}>
            <option value="" disabled>Select a team...</option>
            {teams?.map(t => <option key={t.id} value={t.id}>{t.name}</option>)}
          </select>
        )}
      </div>

      {newSnippet.tags && newSnippet.tags.length > 0 && (
        <div className="flex flex-wrap gap-1.5 shrink-0">
          {newSnippet.tags.map(tag => (
            <span key={tag} className={`flex items-center gap-1 ${theme.bgSubtle} ${theme.textPrimary} px-2 py-1 rounded-full text-[10px] font-mono border ${theme.borderSubtle}`}>
              {tag} <X size={10} className="cursor-pointer hover:text-red-400 transition-colors" onClick={() => removeTag(tag)} />
            </span>
          ))}
        </div>
      )}

      {showHistory && newSnippet.history?.length > 0 && (
        <div className="bg-surface p-2 rounded border border-gray-800 max-h-24 overflow-y-auto custom-scrollbar shrink-0">
          <p className="text-[10px] text-gray-500 mb-1 font-bold uppercase flex items-center gap-1"><AlertCircle size={10} /> Click a version to restore</p>
          {newSnippet.history.map((ver, idx) => (
            <div key={idx} onClick={() => updateField('content', ver)} className={`text-[10px] text-gray-300 truncate cursor-pointer ${theme.textHover} py-1 border-b border-gray-800 last:border-0`}>{ver}</div>
          ))}
        </div>
      )}

      {renderFields()}

      {!!(window.chrome && window.chrome.tabs) && (
        <button onClick={extract} className={`text-[10px] ${theme.textPrimary} text-left hover:underline shrink-0 mt-1`}>
          {newSnippet.vault === 'links' || newSnippet.vault === 'tools' ? '+ Grab current page URL & Selection' : '+ Extract highlighted text from active tab'}
        </button>
      )}

      <div className="flex gap-2 pt-2 border-t border-gray-800 shrink-0">
        <button onClick={onSave} className={`grow ${theme.bgPrimary} ${theme.bgHover} p-2 rounded text-white text-sm font-bold transition flex justify-center items-center gap-2`}>
          Save <span className="text-[10px] font-normal opacity-70">(Ctrl+S)</span>
        </button>

        {newSnippet.id && (
          <button onClick={() => handleDelete(newSnippet.id)} className="p-2 px-3 bg-red-900/30 hover:bg-red-900/80 rounded text-red-300 text-sm transition" title="Delete">
            <Trash2 size={18} />
          </button>
        )}

        <button onClick={onCancel} className="p-2 px-4 bg-gray-800 hover:bg-gray-700 rounded text-gray-300 text-sm transition">Cancel</button>
      </div>
    </div>
  );
};

export default CreateView;